import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { getErrorMessage, useMutationRequest } from "~/v2/api/hooks";
import { cancelOrder } from "~/v2/api/rest/account";
import { type CancelOrderResult, cancelOrders } from "./api";
import type { OrdersWidgetCancellationFeedback } from "./OrdersCancellation.pres";
import type { OrdersWidgetRow } from "./OrdersWidget.types";
import { ORDERS_UNAVAILABLE_VALUE } from "./OrdersWidget.utils";
import { useInvalidateOrderRelatedQueries } from "./useInvalidateOrderRelatedQueries";

const MAX_BATCH_FAILURE_DETAILS = 2;

interface SingleCancelVariables {
  accountID: string;
  product: "live" | "paper";
  orderID: string;
}

interface BatchCancelVariables {
  accountID: string;
  product: "live" | "paper";
  orderIDs: string[];
}

interface ActiveBatchAttempt {
  contextKey: string;
  selectionScopeGeneration: number;
  token: number;
}

const suppressGlobalCancellationToast = () => {
  // Per-call handlers below own account- and attempt-guarded feedback.
};

export const formatFailedBatchResults = (
  failedIds: string[],
  results: CancelOrderResult[],
  identityById: ReadonlyMap<string, { symbol?: string }>,
): string => {
  const resultById = new Map(results.map((result) => [result.id, result]));
  const details = failedIds.slice(0, MAX_BATCH_FAILURE_DETAILS).map((id) => {
    const symbol = identityById.get(id)?.symbol;
    const identity =
      symbol && symbol !== ORDERS_UNAVAILABLE_VALUE ? `${symbol} (${id})` : id;
    const error = resultById.get(id)?.error;
    return error ? `${identity}: ${error}` : identity;
  });
  const overflowCount = failedIds.length - details.length;
  if (overflowCount > 0) {
    details.push(`${overflowCount} more`);
  }
  return details.join("; ");
};

const createBatchCancellationFeedback = ({
  attemptedIds,
  failedIds,
  succeededCount,
  results,
  attemptedIdentityById,
}: {
  attemptedIds: string[];
  failedIds: string[];
  succeededCount: number;
  results: CancelOrderResult[];
  attemptedIdentityById: ReadonlyMap<string, { symbol?: string }>;
}): OrdersWidgetCancellationFeedback => {
  if (failedIds.length === 0) {
    return {
      color: "green",
      message: `Cancelled ${attemptedIds.length} ${
        attemptedIds.length === 1 ? "order" : "orders"
      }.`,
    };
  }

  const failureDetails = formatFailedBatchResults(
    failedIds,
    results,
    attemptedIdentityById,
  );
  if (succeededCount > 0) {
    const failedOrderLabel = failedIds.length === 1 ? "order" : "orders";
    const attemptedOrderLabel = attemptedIds.length === 1 ? "order" : "orders";
    return {
      color: "orange",
      message: `Cancelled ${succeededCount} of ${attemptedIds.length} ${attemptedOrderLabel}. ${failedIds.length} ${failedOrderLabel} could not be cancelled: ${failureDetails}`,
    };
  }

  return {
    color: "red",
    message: `Could not cancel ${attemptedIds.length} order${
      attemptedIds.length === 1 ? "" : "s"
    }: ${failureDetails}`,
  };
};

export const useOrdersCancellationState = ({
  accountID,
  product,
  selectionScopeKey,
}: {
  accountID?: string;
  product?: "live" | "paper";
  selectionScopeKey: string;
}) => {
  const [cancellingIds, setCancellingIds] = useState<string[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [isBatchCancelling, setIsBatchCancelling] = useState(false);
  const [cancellationFeedback, setCancellationFeedback] =
    useState<OrdersWidgetCancellationFeedback>();
  const singleAttemptSequenceRef = useRef(0);
  const singleAttemptTokensRef = useRef(new Map<string, number>());
  const batchAttemptSequenceRef = useRef(0);
  const activeBatchAttemptRef = useRef<ActiveBatchAttempt>();
  const cancellationContextKey =
    accountID && product ? `${product}:${accountID}` : "";
  const cancellationContextKeyRef = useRef(cancellationContextKey);
  cancellationContextKeyRef.current = cancellationContextKey;
  const selectionScopeRef = useRef({
    generation: 0,
    key: selectionScopeKey,
  });
  if (selectionScopeRef.current.key !== selectionScopeKey) {
    selectionScopeRef.current = {
      generation: selectionScopeRef.current.generation + 1,
      key: selectionScopeKey,
    };
  }

  // Account identity is the cancellation boundary. It also invalidates
  // callbacks from requests that settle after the user switches accounts.
  // biome-ignore lint/correctness/useExhaustiveDependencies: accountID/product are reset triggers; the body deliberately does not capture stale identity values
  useEffect(() => {
    singleAttemptTokensRef.current.clear();
    activeBatchAttemptRef.current = undefined;
    setSelectedIds([]);
    setCancellingIds([]);
    setCancellationFeedback(undefined);
    setIsBatchCancelling(false);
  }, [accountID, product]);

  // Selection is intentionally page/filter scoped so hidden orders can never
  // be submitted from a previous view.
  // biome-ignore lint/correctness/useExhaustiveDependencies: selectionScopeKey is the reset trigger
  useEffect(() => {
    setSelectedIds([]);
  }, [selectionScopeKey]);

  return {
    cancellingIds,
    selectedIds,
    isBatchCancelling,
    cancellationFeedback,
    setCancellingIds,
    setSelectedIds,
    setIsBatchCancelling,
    setCancellationFeedback,
    cancellationContextKey,
    cancellationContextKeyRef,
    singleAttemptSequenceRef,
    singleAttemptTokensRef,
    batchAttemptSequenceRef,
    activeBatchAttemptRef,
    selectionScopeRef,
  };
};

export const useOrdersCancellationController = ({
  state,
  rows,
  allowCancel,
  accountID,
  product,
}: {
  state: ReturnType<typeof useOrdersCancellationState>;
  rows: OrdersWidgetRow[];
  allowCancel: boolean;
  accountID?: string;
  product?: "live" | "paper";
}) => {
  const invalidateOrderRelatedQueries = useInvalidateOrderRelatedQueries();
  const {
    cancellingIds,
    selectedIds,
    isBatchCancelling,
    cancellationFeedback,
    setCancellingIds,
    setSelectedIds,
    setIsBatchCancelling,
    setCancellationFeedback,
    cancellationContextKey,
    cancellationContextKeyRef,
    singleAttemptSequenceRef,
    singleAttemptTokensRef,
    batchAttemptSequenceRef,
    activeBatchAttemptRef,
    selectionScopeRef,
  } = state;

  const eligibleIdSet = useMemo(
    () =>
      new Set(
        rows
          .filter((row) => row.canCancel && !row.isLegRow)
          .map((row) => row.id),
      ),
    [rows],
  );
  const selectableIds = useMemo(
    () =>
      rows
        .filter(
          (row) =>
            row.canCancel &&
            !row.isLegRow &&
            !row.isCancelling &&
            !row.isEditing,
        )
        .map((row) => row.id),
    [rows],
  );
  const selectableIdSet = useMemo(
    () => new Set(selectableIds),
    [selectableIds],
  );
  const selectableIdSetRef = useRef(selectableIdSet);
  selectableIdSetRef.current = selectableIdSet;
  const eligibleIdSetRef = useRef(eligibleIdSet);
  eligibleIdSetRef.current = eligibleIdSet;
  const cancellingIdSetRef = useRef(new Set(cancellingIds));
  cancellingIdSetRef.current = new Set(cancellingIds);
  const rowIdentityById = useMemo(
    () => new Map(rows.map((row) => [row.id, { symbol: row.symbol }])),
    [rows],
  );
  const rowIdentityByIdRef = useRef(rowIdentityById);
  rowIdentityByIdRef.current = rowIdentityById;

  useEffect(() => {
    setSelectedIds((current) => {
      const next = current.filter((id) => eligibleIdSet.has(id));
      return next.length === current.length ? current : next;
    });
  }, [eligibleIdSet, setSelectedIds]);

  const { mutate: cancelMutate } = useMutationRequest(
    ({
      accountID: targetAccountID,
      product: targetProduct,
      orderID,
    }: SingleCancelVariables) =>
      cancelOrder({
        accountID: targetAccountID,
        product: targetProduct,
        orderID,
      }),
    { onError: suppressGlobalCancellationToast },
  );

  const handleCancel = useCallback(
    (orderID: string) => {
      if (
        !allowCancel ||
        !accountID ||
        !product ||
        !eligibleIdSetRef.current.has(orderID)
      ) {
        return;
      }

      const contextKey = cancellationContextKey;
      const attemptToken = ++singleAttemptSequenceRef.current;
      singleAttemptTokensRef.current.set(orderID, attemptToken);
      const isCurrentAttempt = () =>
        cancellationContextKeyRef.current === contextKey &&
        singleAttemptTokensRef.current.get(orderID) === attemptToken;

      setCancellationFeedback(undefined);
      setSelectedIds((current) => current.filter((id) => id !== orderID));
      setCancellingIds((current) =>
        current.includes(orderID) ? current : [...current, orderID],
      );
      cancelMutate(
        { accountID, product, orderID },
        {
          onSuccess: () => {
            invalidateOrderRelatedQueries();
            if (!isCurrentAttempt()) {
              return;
            }
            setCancellationFeedback({
              color: "green",
              message: "Order cancelled.",
            });
          },
          onError: (error: Error) => {
            if (!isCurrentAttempt()) {
              return;
            }
            setCancellationFeedback({
              color: "red",
              message: `Order could not be cancelled. ${getErrorMessage(error)}`,
            });
          },
          onSettled: () => {
            if (!isCurrentAttempt()) {
              return;
            }
            singleAttemptTokensRef.current.delete(orderID);
            setCancellingIds((current) =>
              current.filter((id) => id !== orderID),
            );
          },
        },
      );
    },
    [
      accountID,
      allowCancel,
      cancelMutate,
      cancellationContextKey,
      cancellationContextKeyRef,
      invalidateOrderRelatedQueries,
      product,
      setCancellationFeedback,
      setCancellingIds,
      setSelectedIds,
      singleAttemptSequenceRef,
      singleAttemptTokensRef,
    ],
  );

  const { mutate: batchCancelMutate } = useMutationRequest(
    ({
      accountID: targetAccountID,
      product: targetProduct,
      orderIDs,
    }: BatchCancelVariables) =>
      cancelOrders({
        accountID: targetAccountID,
        product: targetProduct,
        orderIDs,
      }),
    { onError: suppressGlobalCancellationToast },
  );

  const handleToggleSelected = useCallback(
    (orderID: string, selected: boolean) => {
      if (
        !selectableIdSetRef.current.has(orderID) ||
        cancellingIds.includes(orderID)
      ) {
        return;
      }
      setSelectedIds((current) => {
        if (selected) {
          return current.includes(orderID) ? current : [...current, orderID];
        }
        return current.filter((id) => id !== orderID);
      });
    },
    [cancellingIds, setSelectedIds],
  );

  const handleSelectAllCancellable = useCallback(
    (selected: boolean) => {
      if (isBatchCancelling) {
        return;
      }
      setSelectedIds(selected ? [...selectableIdSetRef.current] : []);
    },
    [isBatchCancelling, setSelectedIds],
  );

  const handleClearSelected = useCallback(() => {
    if (!isBatchCancelling) {
      setSelectedIds([]);
    }
  }, [isBatchCancelling, setSelectedIds]);

  const handleRequestBatchCancel = useCallback(() => {
    if (!allowCancel || !accountID || !product || isBatchCancelling) {
      return;
    }
    const attemptedIds = selectedIds.filter(
      (id) => eligibleIdSetRef.current.has(id) && !cancellingIds.includes(id),
    );
    if (attemptedIds.length === 0) {
      return;
    }

    const attemptedIdSet = new Set(attemptedIds);
    const attemptedIdentityById = new Map(
      attemptedIds.map((id) => [id, rowIdentityByIdRef.current.get(id) ?? {}]),
    );
    const contextKey = cancellationContextKey;
    const attemptToken = ++batchAttemptSequenceRef.current;
    const selectionScopeGeneration = selectionScopeRef.current.generation;
    activeBatchAttemptRef.current = {
      contextKey,
      selectionScopeGeneration,
      token: attemptToken,
    };
    const isActiveAttempt = () => {
      const activeAttempt = activeBatchAttemptRef.current;
      return (
        activeAttempt?.contextKey === contextKey &&
        activeAttempt.token === attemptToken
      );
    };
    const isCurrentAttempt = () => {
      return (
        cancellationContextKeyRef.current === contextKey &&
        selectionScopeRef.current.generation === selectionScopeGeneration &&
        isActiveAttempt()
      );
    };

    setCancellationFeedback(undefined);
    setIsBatchCancelling(true);
    setCancellingIds((current) => [...new Set([...current, ...attemptedIds])]);
    batchCancelMutate(
      { accountID, product, orderIDs: attemptedIds },
      {
        onSuccess: (results: CancelOrderResult[]) => {
          invalidateOrderRelatedQueries();
          if (!isCurrentAttempt()) {
            return;
          }

          const succeededIds = new Set(
            results
              .filter(
                (result) => result.ok === true && attemptedIdSet.has(result.id),
              )
              .map((result) => result.id),
          );
          const failedIds = attemptedIds.filter((id) => !succeededIds.has(id));
          const succeededCount = attemptedIds.length - failedIds.length;

          setSelectedIds((current) => {
            const retained = current.filter((id) => !attemptedIdSet.has(id));
            const eligibleFailures = failedIds.filter((id) =>
              eligibleIdSetRef.current.has(id),
            );
            return [...new Set([...retained, ...eligibleFailures])];
          });

          setCancellationFeedback(
            createBatchCancellationFeedback({
              attemptedIds,
              failedIds,
              succeededCount,
              results,
              attemptedIdentityById,
            }),
          );
        },
        onError: (error: Error) => {
          if (!isCurrentAttempt()) {
            return;
          }
          setSelectedIds((current) => [
            ...new Set([
              ...current,
              ...attemptedIds.filter((id) => eligibleIdSetRef.current.has(id)),
            ]),
          ]);
          setCancellationFeedback({
            color: "red",
            message: `Orders could not be cancelled. ${getErrorMessage(error)}`,
          });
        },
        onSettled: () => {
          if (!isActiveAttempt()) {
            return;
          }
          activeBatchAttemptRef.current = undefined;
          setIsBatchCancelling(false);
          setCancellingIds((current) =>
            current.filter((id) => !attemptedIdSet.has(id)),
          );
        },
      },
    );
  }, [
    accountID,
    activeBatchAttemptRef,
    allowCancel,
    batchAttemptSequenceRef,
    batchCancelMutate,
    cancellingIds,
    cancellationContextKey,
    cancellationContextKeyRef,
    invalidateOrderRelatedQueries,
    isBatchCancelling,
    product,
    selectedIds,
    selectionScopeRef,
    setCancellationFeedback,
    setCancellingIds,
    setIsBatchCancelling,
    setSelectedIds,
  ]);

  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  const allCancellableSelected =
    selectableIds.length > 0 &&
    selectableIds.every((id) => selectedIdSet.has(id));
  const someCancellableSelected =
    !allCancellableSelected &&
    selectableIds.some((id) => selectedIdSet.has(id));

  return {
    cancellingIds,
    selectedIds,
    isBatchCancelling,
    cancellationFeedback,
    setCancellationFeedback,
    selectedIdSet,
    allCancellableSelected,
    someCancellableSelected,
    handleCancel,
    handleToggleSelected,
    handleSelectAllCancellable,
    handleClearSelected,
    handleRequestBatchCancel,
    cancellingIdSetRef,
  };
};
